import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { usersApi, leavePoliciesApi } from '../../services/api';
import useStore from '../../store';
import './UserProfile.css';

const PERSONAL_FIELDS = ['mobile_number','address','temporary_address','emergency_contact_name','emergency_contact_number','emergency_contact_relation'];
const ADMIN_FIELDS = [...PERSONAL_FIELDS, 'first_name','last_name','designation','company','role','employee_type','joining_date','date_of_birth','is_active','manager_id'];

export default function UserProfilePage({ viewUserId }) {
  const { user, fetchMe } = useStore();
  const isAdmin = user?.role === 'admin';
  const targetId = viewUserId || user?.id;
  const isSelf = targetId === user?.id;

  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [leavePolicies, setLeavePolicies] = useState([]);
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [profRes, deptRes, countryRes, policyRes, mgrRes] = await Promise.all([
        usersApi.get(targetId),
        usersApi.departments(),
        usersApi.countries(),
        isAdmin ? leavePoliciesApi.list() : Promise.resolve({ data: { data: [] } }),
        usersApi.list({ role: 'manager', limit: 1000 }),
      ]);
      setProfile(profRes.data.data);
      setDepartments(deptRes.data.data);
      setCountries(countryRes.data.data);
      setLeavePolicies(policyRes.data.data || []);
      setManagers(mgrRes.data.data || []);
    };
    if (targetId) load();
  }, [targetId, isAdmin]);

  const startEdit = () => {
    if (!profile) return;
    setForm({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      designation: profile.designation || '',
      company: profile.company || '',
      mobile_number: profile.mobile_number || '',
      address: profile.address || '',
      temporary_address: profile.temporary_address || '',
      emergency_contact_name: profile.emergency_contact_name || '',
      emergency_contact_number: profile.emergency_contact_number || '',
      emergency_contact_relation: profile.emergency_contact_relation || '',
      date_of_birth: profile.date_of_birth || '',
      role: profile.role || 'employee',
      employee_type: profile.employee_type || 'full_time',
      joining_date: profile.joining_date || '',
      department_id: profile.department_id || '',
      leave_policy_id: profile.leave_policy_id || profile.leavePolicy?.id || '',
      country_id: profile.country_id || '',
      manager_id: profile.manager?.id || '',
      is_active: profile.is_active,
    });
    setEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const allowed = isAdmin ? ADMIN_FIELDS : PERSONAL_FIELDS;
      const payload = {};
      allowed.forEach((f) => { if (form[f] !== undefined) payload[f] = form[f]; });
      if (isAdmin) {
        payload.department_id = form.department_id || null;
        payload.leave_policy_id = form.leave_policy_id || null;
        payload.country_id = form.country_id || null;
        payload.manager_id = form.manager_id || null;
        payload.date_of_birth = form.date_of_birth || null;
        payload.joining_date = form.joining_date || null;
      }
      await usersApi.update(targetId, payload);
      toast.success('Profile updated!');
      if (isSelf) await fetchMe();
      const res = await usersApi.get(targetId);
      setProfile(res.data.data);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return <div className="skeleton" style={{ height: 400, borderRadius: 16 }} />;
  }

  const initials = `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase();

  return (
    <div className="profile-page">
      <div className="page-header">
        <div>
          <div className="page-title">{isSelf ? 'My Profile' : `${profile.first_name}'s Profile`}</div>
          <div className="page-subtitle">
            {isSelf ? 'View and update your personal information' : 'Employee profile details'}
          </div>
        </div>
        {!editing && (
          <button className="btn btn-primary" onClick={startEdit}>
            ✏️ Edit Profile
          </button>
        )}
      </div>

      <div className="profile-layout">
        {/* Left: Avatar + summary */}
        <div className="card profile-card-left animate-in">
          <div className="profile-avatar-section">
            <div className="avatar avatar-xl profile-avatar">
              {profile.avatar_url
                ? <img src={profile.avatar_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                : initials
              }
            </div>
            <h2>{profile.first_name} {profile.last_name}</h2>
            <p className="profile-designation">{profile.designation || '—'}</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', marginTop: 8 }}>
              <span className="badge badge-violet capitalize">{profile.role}</span>
              <span className={`badge ${profile.employee_type === 'contractor' ? 'badge-amber' : 'badge-sky'}`}>
                {profile.employee_type === 'contractor' ? 'Contractor' : 'Full Time'}
              </span>
              {!profile.is_active && <span className="badge badge-rose">Inactive</span>}
            </div>
          </div>

          <div className="profile-meta">
            <div className="profile-meta-item">
              <span className="profile-meta-label">Employee ID</span>
              <span className="profile-meta-value">{profile.id || '—'}</span>
            </div>
            <div className="profile-meta-item">
              <span className="profile-meta-label">Department</span>
              <span className="profile-meta-value">{profile.department?.name || '—'}</span>
            </div>
            <div className="profile-meta-item">
              <span className="profile-meta-label">Company</span>
              <span className="profile-meta-value">{profile.company || '—'}</span>
            </div>
            <div className="profile-meta-item">
              <span className="profile-meta-label">Leave Policy</span>
              <span className="profile-meta-value">{profile.leavePolicy?.name || '—'}</span>
            </div>
            <div className="profile-meta-item">
              <span className="profile-meta-label">Country</span>
              <span className="profile-meta-value">{profile.country?.name || '—'}</span>
            </div>
            <div className="profile-meta-item">
              <span className="profile-meta-label">Manager</span>
              <span className="profile-meta-value">
                {profile.manager ? `${profile.manager.first_name} ${profile.manager.last_name}` : '—'}
              </span>
            </div>
            <div className="profile-meta-item">
              <span className="profile-meta-label">Manager Contact Number</span>
              <span className="profile-meta-value">
                {profile.manager?.mobile_number || '—'}
              </span>
            </div>
            <div className="profile-meta-item">
              <span className="profile-meta-label">Joined</span>
              <span className="profile-meta-value">
                {profile.joining_date ? new Date(profile.joining_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
              </span>
            </div>
            <div className="profile-meta-item">
              <span className="profile-meta-label">Email</span>
              <span className="profile-meta-value" style={{ wordBreak: 'break-all' }}>{profile.email}</span>
            </div>
          </div>
        </div>

        {/* Right: Detail form */}
        <div className="profile-card-right animate-in stagger-1">
          {!editing ? (
            <>
              <ProfileSection title="Contact Information">
                <InfoRow label="Mobile" value={profile.mobile_number} />
                <InfoRow label="Address" value={profile.address} />
                <InfoRow label="Temporary Address" value={profile.temporary_address} />
                <InfoRow label="Date of Birth" value={profile.date_of_birth ? new Date(profile.date_of_birth).toLocaleDateString('en-IN') : null} />
              </ProfileSection>

              <ProfileSection title="Emergency Contact">
                <InfoRow label="Name" value={profile.emergency_contact_name} />
                <InfoRow label="Phone" value={profile.emergency_contact_number} />
                <InfoRow label="Relation" value={profile.emergency_contact_relation} />
              </ProfileSection>

              {isAdmin && (
                <ProfileSection title="Employment Details">
                  <InfoRow label="Employee Type" value={profile.employee_type} capitalize />
                  <InfoRow label="Role" value={profile.role} capitalize />
                  <InfoRow label="Joining Date" value={profile.joining_date ? new Date(profile.joining_date).toLocaleDateString('en-IN') : null} />
                  <InfoRow label="Date of Birth" value={profile.date_of_birth ? new Date(profile.date_of_birth).toLocaleDateString('en-IN') : null} />
                  <InfoRow label="Active" value={profile.is_active ? 'Yes' : 'No'} />
                </ProfileSection>
              )}
            </>
          ) : (
            <form onSubmit={handleSave} className="profile-form">
              {isAdmin && (
                <ProfileSection title="Employment Details">
                  <div className="profile-form-grid">
                    <div className="form-group">
                      <label className="form-label">First Name</label>
                      <input className="form-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last Name</label>
                      <input className="form-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Designation</label>
                      <input className="form-input" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Company</label>
                      <select className="form-select" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}>
                        <option value="">- Select -</option>
                        <option value="Aays">Aays</option>
                        <option value="Aidetic">Aidetic</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Role</label>
                      <select className="form-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                        <option value="admin">Admin</option>
                        <option value="manager">Manager</option>
                        <option value="employee">Employee</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Employee Type</label>
                      <select className="form-select" value={form.employee_type} onChange={(e) => setForm({ ...form, employee_type: e.target.value })}>
                        <option value="full_time">Full Time</option>
                        <option value="contractor">Contractor</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Joining Date</label>
                      <input type="date" className="form-input" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Date of Birth</label>
                      <input type="date" className="form-input" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <select className="form-select" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
                        <option value="">— Select —</option>
                        {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Leave Policy</label>
                      <select className="form-select" value={form.leave_policy_id} onChange={(e) => setForm({ ...form, leave_policy_id: e.target.value })}>
                        <option value="">— Select —</option>
                        {leavePolicies.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Country</label>
                      <select className="form-select" value={form.country_id} onChange={(e) => setForm({ ...form, country_id: e.target.value })}>
                        <option value="">— Select —</option>
                        {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Manager</label>
                      <select className="form-select" value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })}>
                        <option value="">— Select —</option>
                        {managers.map((m) => (
                          <option key={m.id} value={m.id}>{m.first_name} {m.last_name} {m.email ? `(${m.email})` : ''}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
                        <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                        Active
                      </label>
                    </div>
                  </div>
                </ProfileSection>
              )}

              <ProfileSection title="Contact Information">
                <div className="profile-form-grid">
                  <div className="form-group">
                    <label className="form-label">Mobile Number</label>
                    <input className="form-input" placeholder="+91-XXXXXXXXXX" value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value })} />
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="form-label">Address</label>
                  <textarea className="form-textarea" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                 <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="form-label">Temporary Address</label>
                  <textarea className="form-textarea" rows={2} value={form.temporary_address} onChange={(e) => setForm({ ...form, temporary_address: e.target.value })} />
                </div>
              </ProfileSection>

              <ProfileSection title="Emergency Contact">
                <div className="profile-form-grid">
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input className="form-input" value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-input" value={form.emergency_contact_number} onChange={(e) => setForm({ ...form, emergency_contact_number: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Relation</label>
                    <input className="form-input" placeholder="e.g. Spouse, Parent" value={form.emergency_contact_relation} onChange={(e) => setForm({ ...form, emergency_contact_relation: e.target.value })} />
                  </div>
                </div>
              </ProfileSection>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '0 0 8px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileSection({ title, children }) {
  return (
    <div className="profile-section card">
      <div className="profile-section-title">{title}</div>
      {children}
    </div>
  );
}

function InfoRow({ label, value, capitalize }) {
  return (
    <div className="info-row">
      <span className="info-row-label">{label}</span>
      <span className="info-row-value" style={capitalize ? { textTransform: 'capitalize' } : {}}>
        {value || <span style={{ color: 'var(--text-muted)' }}>Not provided</span>}
      </span>
    </div>
  );
}
